import { Box, Button, Heading, HStack, Text, Textarea, VStack } from "@chakra-ui/react"
import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useBulkImportHomes, useHomes, useLatestProcessingBatchImport } from "../hooks/usePortfolioQueries"

const MIN_ADDRESS_LENGTH = 5

const normalize = (s: string) =>
  s.toLowerCase().replace(/[,.\s]+/g, " ").trim()

const UK_POSTCODE_RE =
  /[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}/i

const extractPostcode = (address: string): string => {
  const match = address.match(UK_POSTCODE_RE)
  return match ? match[0].trim() : ""
}

const cleanLine = (line: string) =>
  line
    .replace(/\t+/g, ", ")     // tabs → commas (for spreadsheet columns)
    .replace(/^["']+|["']+$/g, "") // strip wrapping quotes
    .replace(/,\s*$/, "")      // trailing comma
    .trim()

const parseAddresses = (text: string) =>
  text
    .split("\n")
    .map(cleanLine)
    .filter((line) => line.length >= MIN_ADDRESS_LENGTH)

const BulkImport: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const portfolioId = (location.state as { portfolioId?: number } | null)?.portfolioId
  const { data: latestBatch, isLoading: isCheckingBatch } = useLatestProcessingBatchImport()
  const bulkImportMutation = useBulkImportHomes()
  const { data: homesData } = useHomes()
  const [addressText, setAddressText] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [quotaError, setQuotaError] = useState<{
    quotaLimit: number
    currentCount: number
    addressesToAdd: number
    remainingQuota: number
  } | null>(null)
  const [success, setSuccess] = useState(false)

  const addresses = useMemo(() => parseAddresses(addressText), [addressText])

  const existingAddresses = useMemo(() => {
    const homes = Array.isArray(homesData?.homes) ? homesData.homes : []
    return new Set(homes.map((h) => normalize(h.fullAddress)))
  }, [homesData])

  const alreadyInPortfolio = useMemo(
    () => addresses.filter((a) => existingAddresses.has(normalize(a))),
    [addresses, existingAddresses]
  )

  const duplicates = useMemo(() => {
    const seen = new Map<string, number>()
    for (const addr of addresses) {
      const key = addr.toLowerCase()
      seen.set(key, (seen.get(key) || 0) + 1)
    }
    return [...new Set(addresses.filter((a) => (seen.get(a.toLowerCase()) || 0) > 1))]
  }, [addresses])

  const missingPostcode = useMemo(
    () => addresses.filter((a) => !UK_POSTCODE_RE.test(a)),
    [addresses]
  )

  const missingParts = useMemo(
    () => addresses.filter((a) => a.split(",").map((p) => p.trim()).filter(Boolean).length < 3),
    [addresses]
  )

  const hasWarnings = duplicates.length > 0 || missingPostcode.length > 0 || missingParts.length > 0

  useEffect(() => {
    if (latestBatch?.id) {
      navigate(`/batch-import/${latestBatch.id}`, { replace: true })
    }
  }, [latestBatch, navigate])

  const handleRemoveDuplicates = () => {
    const seen = new Set<string>()
    const deduped = addressText
      .split("\n")
      .map(cleanLine)
      .filter((line) => {
        if (line.length < MIN_ADDRESS_LENGTH) return true // keep blank/short lines as-is
        const key = line.toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    setAddressText(deduped.join("\n"))
  }

  const handleSubmit = async () => {
    if (addresses.length === 0) {
      setError("Please enter at least one address.")
      return
    }

    if (hasWarnings) return

    setError(null)
    setQuotaError(null)
    setSuccess(false)

    try {
      const payload = addresses.map((a) => ({
        full_address: a,
        postcode: extractPostcode(a),
      }))
      const result = await bulkImportMutation.mutateAsync({ addresses: payload, portfolioId })
      console.log("Bulk import response:", result)
      if (!result?.batchImportId) {
        setError("Import started but no batch ID returned. Please check your portfolio.")
        return
      }
      navigate(`/batch-import/${result.batchImportId}`)
    } catch (err: any) {
      const data = err?.response?.data

      // Check if it's a quota error
      if (data?.quota_limit !== undefined) {
        setQuotaError({
          quotaLimit: data.quota_limit,
          currentCount: data.current_count,
          addressesToAdd: data.addresses_to_add,
          remainingQuota: data.remaining_quota,
        })
        setError(null)
      } else {
        const message =
          data?.error ||
          data?.detail ||
          "There was an error importing your homes. Please try again."
        setError(message)
      }
    }
  }

  if (isCheckingBatch || latestBatch?.id) {
    return (
      <Box p={8}>
        <Text color="gray.500">Checking for active imports...</Text>
      </Box>
    )
  }

  return (
    <Box p={8}>
      <VStack gap={6} align="stretch">
        <Box>
          <Button
            variant="ghost"
            onClick={() => navigate("/portfolio")}
            mb={4}
            alignSelf="flex-start"
          >
            ← Back to Portfolio
          </Button>
          <Heading size="xl">Bulk Import Homes</Heading>
          <Text color="gray.600" mt={2}>
            You can copy directly from your spreadsheet, but <strong>make sure your columns are formatted like the following example</strong>:
          </Text>
          <Box as="table" mt={3} fontSize="sm" borderCollapse="collapse">
            <thead>
              <tr>
                <Box as="th" bg="gray.100" border="1px solid" borderColor="gray.300" px={3} py={2} fontWeight="medium" textAlign="left">
                  House number & street
                </Box>
                <Box as="th" bg="gray.100" border="1px solid" borderColor="gray.300" px={3} py={2} fontWeight="medium" textAlign="left">
                  Town/City
                </Box>
                <Box as="th" bg="gray.100" border="1px solid" borderColor="gray.300" px={3} py={2} fontWeight="medium" textAlign="left">
                  Postcode
                </Box>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Box as="td" bg="white" border="1px solid" borderColor="gray.300" px={3} py={2} color="gray.600">
                  10 Downing Street
                </Box>
                <Box as="td" bg="white" border="1px solid" borderColor="gray.300" px={3} py={2} color="gray.600">
                  London
                </Box>
                <Box as="td" bg="white" border="1px solid" borderColor="gray.300" px={3} py={2} color="gray.600">
                  SW1A 2AA
                </Box>
              </tr>
            </tbody>
          </Box>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <VStack gap={4} align="stretch">
            <Textarea
              placeholder={"10 Downing Street, London, SW1A 2AA\n221B Baker Street, London, NW1 6XE"}
              value={addressText}
              autoFocus
              onChange={(e) => {
                setError(null)
                setQuotaError(null)
                setSuccess(false)
                setAddressText(e.target.value)
              }}
              onPaste={(e) => {
                e.preventDefault()
                const pasted = e.clipboardData.getData("text/plain")
                const cleaned = pasted
                  .split(/\r?\n/)
                  .map(cleanLine)
                  .join("\n")
                setError(null)
                setQuotaError(null)
                setSuccess(false)
                setAddressText((prev) =>
                  prev ? prev + "\n" + cleaned : cleaned
                )
              }}
              rows={10}
            />

            {duplicates.length > 0 && (
              <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={3}>
                <HStack justify="space-between" align="start">
                  <Box>
                    <Text color="red.700" fontSize="sm" fontWeight="bold">
                      Duplicate addresses found:
                    </Text>
                    {duplicates.map((addr, i) => (
                      <Text key={i} color="red.600" fontSize="sm">
                        {addr}
                      </Text>
                    ))}
                  </Box>
                  <Button size="sm" variant="outline" colorPalette="red" onClick={handleRemoveDuplicates}>
                    Remove duplicates
                  </Button>
                </HStack>
              </Box>
            )}

            {missingPostcode.length > 0 && (
              <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                <Text color="orange.700" fontSize="sm" fontWeight="bold">
                  Missing or invalid UK postcode ({missingPostcode.length}):
                </Text>
                {missingPostcode.map((addr, i) => (
                  <Text key={i} color="orange.600" fontSize="sm">
                    {addr}
                  </Text>
                ))}
              </Box>
            )}

            {missingParts.length > 0 && (
              <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                <Text color="orange.700" fontSize="sm" fontWeight="bold">
                  Missing address parts - need street, town/city, and postcode ({missingParts.length}):
                </Text>
                {missingParts.map((addr, i) => (
                  <Text key={i} color="orange.600" fontSize="sm">
                    {addr}
                  </Text>
                ))}
              </Box>
            )}

            {alreadyInPortfolio.length > 0 && (
              <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" p={3}>
                <Text color="blue.700" fontSize="sm" fontWeight="bold">
                  Already in your portfolio ({alreadyInPortfolio.length}):
                </Text>
                {alreadyInPortfolio.map((addr, i) => (
                  <Text key={i} color="blue.600" fontSize="sm">
                    {addr}
                  </Text>
                ))}
              </Box>
            )}

            {quotaError && (
              <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={4}>
                <Text color="red.700" fontSize="sm" fontWeight="bold" mb={2}>
                  This import would exceed your home quota
                </Text>
                <VStack align="start" gap={1}>
                  <Text color="red.600" fontSize="sm">
                    Your quota: {quotaError.currentCount} / {quotaError.quotaLimit} homes used
                  </Text>
                  <Text color="red.600" fontSize="sm">
                    Addresses to import: {quotaError.addressesToAdd}
                  </Text>
                  <Text color="red.600" fontSize="sm">
                    Remaining quota: {quotaError.remainingQuota}
                  </Text>
                </VStack>
                <Text color="red.700" fontSize="sm" mt={2}>
                  Please reduce the number of addresses or contact support to increase your quota.
                </Text>
              </Box>
            )}

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}

            {success && (
              <Text color="green.600" fontSize="sm">
                Homes imported successfully!
              </Text>
            )}

            <Button
              bg="green.800"
              color="white"
              _hover={{ bg: "green.700" }}
              onClick={handleSubmit}
              loading={bulkImportMutation.isPending}
              disabled={addresses.length === 0 || hasWarnings || bulkImportMutation.isPending}
            >
              {addresses.length === 0
                ? "Import"
                : `Import ${addresses.length} address${addresses.length === 1 ? "" : "es"}`}
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Box>
  )
}

export default BulkImport
